
import React, { useEffect, useState } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from '../../../services/api';
import { buildD8Payload } from '../../../services/payloads/d8Payload';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d8/d8.css';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';
import EmailPopup from '../../../sharedcomponents/email-popup/EmailPopup';
import { getCurrentUserName } from '../../../services/auth';

const D8 = ({ expanded, onSetClick, handleCloseForm, onToggleAccordion, isEnabled = false, onSaved, eightDInfoId, currentUserEmail, d8Data, d8ResponsibleData, mode, teamMembers: teamMembersProp = [], d2ResponsibleData, d3ResponsibleData, d4ResponsibleData, d5ResponsibleData, d6ResponsibleData, d7ResponsibleData, readOnly = false }) => {
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  const [appreciationInput, setAppreciationInput] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [responsiblePerson, setResponsiblePerson] = useState(getCurrentUserName() || currentUserEmail || '');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [btnValue, setBtnValue] = useState('Save');
  const [d8Id, setD8Id] = useState(null);
  const [formSaved, setFormSaved] = useState(false);
  const [achievedDateAlert, setAchievedDateAlert] = useState(false);
  const [missingAchievedForms, setMissingAchievedForms] = useState([]);
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [problemStatement, setProblemStatement] = useState('');

  // Block form when readOnly (user is not originator)
  const isFormBlocked = readOnly;

  // Auto-populate Responsible Person and Planned Date from D2-D7 responsible data
  useEffect(() => {
    // Only skip if D8 already has a saved responsible person
    const savedRP =
      (d8Data && (d8Data.ResponsiblePerson || d8Data.responsiblePerson)) ||
      (() => { const r = Array.isArray(d8ResponsibleData) ? d8ResponsibleData[0] : d8ResponsibleData; return r?.ResponsiblePerson; })();
    if (savedRP) return;

    // Prefill with originator (currentUserEmail)
    if (currentUserEmail) {
      setResponsiblePerson(getCurrentUserName() || currentUserEmail);
      return;
    }

    const allResponsible = [
      { data: d2ResponsibleData },
      { data: d3ResponsibleData },
      { data: d4ResponsibleData },
      { data: d5ResponsibleData },
      { data: d6ResponsibleData },
      { data: d7ResponsibleData },
    ];
    const first = allResponsible.find(({ data }) => {
      const item = Array.isArray(data) ? data[0] : data;
      return !!(item?.ResponsiblePerson || item?.responsiblePerson);
    });
    if (first) {
      const item = Array.isArray(first.data) ? first.data[0] : first.data;
      const rp = item.ResponsiblePerson || item.responsiblePerson || '';
      const pd = item.PlannedDate || item.plannedDate || null;
      if (rp) setResponsiblePerson(rp);
      if (pd) setPlannedDate(new Date(pd));
    }
  }, [d2ResponsibleData, d3ResponsibleData, d4ResponsibleData, d5ResponsibleData, d6ResponsibleData, d7ResponsibleData, d8Data, d8ResponsibleData]);

  const handlePlannedDateChange = async (date) => {
    setPlannedDate(date);
    if (!date || !eightDInfoId) return;
    try {
      const body = JSON.stringify({ EightDInfoId: Number(eightDInfoId) });
      const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body };
      const [d3Resp, d3RespResp, d4Resp, d5Resp, d5RespResp, d6Resp, d6RespResp, d7Resp, d7RespResp] = await Promise.all([
        fetchApi('/api/D3/GetD3', opts).catch(() => null),
        fetchApi('/api/D3/GetD3Responsible', opts).catch(() => null),
        fetchApi('/api/D4/GetD4', opts).catch(() => null),
        fetchApi('/api/D5/GetD5', opts).catch(() => null),
        fetchApi('/api/D5/GetD5Responsible', opts).catch(() => null),
        fetchApi('/api/D6/GetD6', opts).catch(() => null),
        fetchApi('/api/D6/GetD6Responsible', opts).catch(() => null),
        fetchApi('/api/D7/GetD7', opts).catch(() => null),
        fetchApi('/api/D7/GetD7Responsible', opts).catch(() => null),
      ]);
      const extract = (resp) => Array.isArray(resp?.data) ? resp.data[0] : resp?.data;
      const merge = (main, resp) => {
        const m = extract(main) || {};
        const r = extract(resp) || {};
        return {
          ResponsiblePerson: m.ResponsiblePerson || r.ResponsiblePerson || m.responsiblePerson || r.responsiblePerson,
          AchievedDate: m.AchievedDate || r.AchievedDate || m.achievedDate || r.achievedDate,
        };
      };
      const liveData = [
        { key: 'D3', item: merge(d3Resp, d3RespResp) },
        { key: 'D4', item: merge(d4Resp, null) },
        { key: 'D5', item: merge(d5Resp, d5RespResp) },
        { key: 'D6', item: merge(d6Resp, d6RespResp) },
        { key: 'D7', item: merge(d7Resp, d7RespResp) },
      ];
      const missing = liveData
        .filter(({ item }) =>
          item.ResponsiblePerson &&
          !item.AchievedDate
        )
        .map(({ key }) => key);
      setMissingAchievedForms(missing);
      setAchievedDateAlert(missing.length > 0);
    } catch {
      // silently ignore
    }
  };

  // Seed team members from prop whenever it changes
  useEffect(() => {
    if (teamMembersProp && teamMembersProp.length > 0) {
      setTeamMembers(teamMembersProp.map(m => ({
        ...m,
        TeamMemberName: m.TeamMemberName || m.teamMemberName || m.name || '',
        EmailId: m.EmailId || m.emailId || m.email || '',
        checked: true
      })));
    }
  }, [teamMembersProp]);

  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d8Data && !d8ResponsibleData && mode === 'edit') {
      const fetchD8Data = async () => {
        try {
          
          const response = await fetchApi('/api/D8/GetD8', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          
          if (response?.data) {
            const d8 = response.data;
            if (d8.D8Id || d8.d8Id) { setD8Id(d8.D8Id || d8.d8Id); setFormSaved(true); }
            setAppreciationInput(d8.AppreciationComment || '');
            setResponsiblePerson(d8.ResponsiblePerson || '');
            setPlannedDate(d8.PlannedDate ? new Date(d8.PlannedDate) : null);
            setAchievedDate(d8.AchievedDate ? new Date(d8.AchievedDate) : null);
            setIsDisabled(d8.IsDisabled || false);
            if (d8.Attachments) {
              const existingFiles = d8.Attachments.map(att => ({
                ...att,
                isExisting: true,
                preview: att.AttachmentData
              }));
              setUploadedFiles(existingFiles);
            }
          }
          
          // Fetch team members from D1 only if not provided via prop
          if (teamMembersProp.length === 0) {
            const d1Response = await fetchApi('/api/D1/GetD1', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
            });
            if (d1Response?.data) {
              setTeamMembers(d1Response.data.map(member => ({
                ...member,
                TeamMemberName: member.TeamMemberName || member.teamMemberName || '',
                EmailId: member.EmailId || member.emailId || '',
                checked: true
              })));
            }
          }
        } catch (error) {
        }
      };
      fetchD8Data();
      return;
    }
    
    // Handle props data
    if (d8Data) {
      if (d8Data.D8Id || d8Data.d8Id) { setD8Id(d8Data.D8Id || d8Data.d8Id); setFormSaved(true); }
      setAppreciationInput(d8Data.AppreciationComment || '');
      setResponsiblePerson(d8Data.ResponsiblePerson || '');
      setPlannedDate(d8Data.PlannedDate ? new Date(d8Data.PlannedDate) : null);
      setAchievedDate(d8Data.AchievedDate ? new Date(d8Data.AchievedDate) : null);
      setIsDisabled(d8Data.IsDisabled || false);
      if (d8Data.Attachments) {
        const existingFiles = d8Data.Attachments.map(att => ({
          ...att,
          isExisting: true,
          preview: att.AttachmentData
        }));
        setUploadedFiles(existingFiles);
      }
    }
    
    if (d8ResponsibleData) {
      const respData = Array.isArray(d8ResponsibleData) ? d8ResponsibleData[0] : d8ResponsibleData;
      if (respData) {
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
    
    // Fetch team members from API only if not provided via prop
    if (eightDInfoId && teamMembersProp.length === 0) {
      const fetchTeamMembers = async () => {
        try {
          const d1Response = await fetchApi('/api/D1/GetD1', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          if (d1Response?.data) {
            setTeamMembers(d1Response.data.map(member => ({
              ...member,
              TeamMemberName: member.TeamMemberName || member.teamMemberName || '',
              EmailId: member.EmailId || member.emailId || '',
              checked: true
            })));
          }
        } catch (error) {
        }
      };
      fetchTeamMembers();
    }
  }, [eightDInfoId, d8Data, d8ResponsibleData]);

  useEffect(() => {
    if (achievedDate && plannedDate && responsiblePerson) {
      setBtnValue('Submit');
    } else {
      setBtnValue('Save');
    }
  }, [achievedDate, plannedDate, responsiblePerson]);

  const handleTeamMemberChange = (index, checked) => {
    setTeamMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, checked } : member
    ));
  };

  // Fetch report info for email body
  useEffect(() => {
    if (!eightDInfoId) return;
    const fetchReportInfo = async () => {
      try {
        const resp = await fetchApi('/api/EightDHeader/Get8DHeader', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
        });
        if (resp?.data) {
          setReportNo(resp.data.ReportNo || resp.data.reportNo || '');
          setProblemStatement(resp.data.ProblemStatement || resp.data.problemStatement || '');
        }
      } catch { /* ignore */ }
    };
    fetchReportInfo();
  }, [eightDInfoId]);

  const handleSave = async () => {
    setSaveError(null);
    setSaveNotice(null);

    if (!plannedDate) {
      setSaveError('Please assign Planned Date');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    if (achievedDateAlert && missingAchievedForms.length > 0) {
      setSaveError(`Please set Achieved Date for: ${missingAchievedForms.join(', ')} before saving D8.`);
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    // If achievedDate is set, show email popup before saving
    if (achievedDate) {
      setEmailPopupOpen(true);
      return;
    }

    await submitD8({ isMailSent: false, toList: [], ccList: [] });
  };

  const handleEmailPopupClose = async (result) => {
    setEmailPopupOpen(false);
    if (result.isMailSent) {
      await submitD8(result);
    } else {
      // Cancel: save without achievedDate
      await submitD8({ isMailSent: false, toList: [], ccList: [], clearAchievedDate: true });
    }
  };

  const submitD8 = async ({ isMailSent = false, toList = [], ccList = [], clearAchievedDate = false }) => {
    setSaving(true);
    try {
      const checkedMembers = teamMembers.filter(m => m.checked);
      const payload = buildD8Payload({
        eightDInfoId,
        appreciationInput,
        responsiblePerson,
        plannedDate,
        achievedDate: clearAchievedDate ? null : achievedDate,
        uploadedFiles,
        isDisabled,
        attachmentsToRemove,
        currentUserEmail: responsiblePerson || currentUserEmail,
        d8Id,
        toList: isMailSent ? checkedMembers.map(m => ({ D1Id: m.D1Id || m.d1Id || 0, EmailId: m.EmailId || m.emailId })) : [],
        ccList: isMailSent ? ccList : [],
        isMailSent,
        reportNo,
        problemStatement,
      });

      const endpoint = d8Id ? '/api/D8/UpdateD8' : '/api/D8/CreateD8';
      const data = await fetchApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (data?.data?.D8Id || data?.data?.d8Id) setD8Id(data.data.D8Id || data.data.d8Id);

      // Re-fetch to get attachment IDs
      try {
        const refreshed = await fetchApi('/api/D8/GetD8', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
        });
        if (refreshed?.data?.Attachments) {
          setUploadedFiles(refreshed.data.Attachments.map(att => ({ ...att, isExisting: true, preview: att.AttachmentData })));
        }
      } catch (e) { }

      setAttachmentsToRemove([]);

      if (!clearAchievedDate && achievedDate) {
        setSaveNotice(isMailSent ? 'Data saved successfully with 8D closure.' : 'Data saved successfully without closing 8D.');
        setTimeout(() => setSaveNotice(null), 2000);
        setFormSaved(true);
      } else {
        setSaveNotice('Data saved successfully.');
        setTimeout(() => setSaveNotice(null), 2000);
        setFormSaved(true);
      }

      if (onSaved) onSaved();
    } catch (error) {
      setSaveError(error?.message || 'Failed to save D8.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    if (canView) {
      onToggleAccordion();
    }
  };

  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (d8Id || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D8</span>
          <span className="d3-subtitle">Close Problem and Recognize contributions</span>
          <Tooltip
            title="Implement containment to control the situation and mitigate further negative effects of the problem."
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>
        {/* Report Closure Button - only show in edit mode */}
        {mode === 'edit' && (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled}>Report Closure</Button>
          </Box>
        )}
      </Box>
      {achievedDateAlert && (
        <Alert severity="warning" onClose={() => setAchievedDateAlert(false)} sx={{ mt: 1, mb: 1 }}>
          Achieved Date is not set for: {missingAchievedForms.join(', ')}. Please update before closing D8.
        </Alert>
      )}

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {expanded && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary>
            <Box className="accordion-close-button-container">
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={handleCloseForm}
              >
                Close Form
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-question-text">All team members</span>
              </Box>
            </Box>

            <TableContainer sx={{ mt: 2, mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Team Member Name</TableCell>
                    <TableCell>Email ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((member, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          checked={member.checked}
                          onChange={(e) => handleTeamMemberChange(index, e.target.checked)}
                          disabled={isDisabled}
                        />
                      </TableCell>
                      <TableCell>{member.TeamMemberName}</TableCell>
                      <TableCell>{member.EmailId}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box className="appreciations-comments">
              <Typography component="label" gutterBottom sx={{ fontFamily: '"GEG Body Copy"' }}>
                Appreciation comments
              </Typography>
              <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                <TextField
                  className="customTextarea"
                  value={appreciationInput}
                  onChange={(e) => setAppreciationInput(e.target.value)}
                  multiline
                  variant="standard"
                  disabled={isDisabled}
                  fullWidth
                />
              </FormControl>
            </Box>

            <ImageUpload
              onFilesSelected={(files) => {
                uploadedFiles.forEach(ef => {
                  const efId = ef.AttachmentId || ef.attachmentId;
                  if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                    setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                  }
                });
                setUploadedFiles(files);
              }}
              folderPath="D8"
              existingFiles={uploadedFiles}
              disabled={isDisabled}
              maxFiles={3}
              rejectedExtensions={['doc', 'docx']}
            />

            <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Responsible Person"
                    value={responsiblePerson}
                    fullWidth
                    disabled
                    variant="standard"
                  />
                  <TwoMonthDatePicker
                    label="Planned Date"
                    value={plannedDate}
                    onChange={handlePlannedDateChange}
                    disabled={isDisabled}
                  />
                  <TwoMonthDatePicker
                    label="Achieved Date"
                    value={achievedDate}
                    onChange={setAchievedDate}
                    disabled={isDisabled || !plannedDate}
                    maxDate={new Date()}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: expanded && !achievedDate ? '#d32f2f' : '#777' } }}
                    sx={expanded && !achievedDate ? {
                      '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
                      '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
                      '& .MuiInputLabel-root': { color: '#d32f2f !important' },
                    } : {}}
                  />
                </Box>
            </Box>

            {!isDisabled && !readOnly && !isFormBlocked && (
              <Box className="save-btn-container">
                <Button 
                  variant="contained" 
                  className="save-btn" 
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    backgroundColor: '#800040',
                    color: '#fff',
                    borderRadius: '20px',
                    textTransform: 'none',
                    px: 4
                  }}
                >
                  {saving ? 'Saving...' : btnValue}
                </Button>
              </Box>
            )}

            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <EmailPopup
        open={emailPopupOpen}
        onClose={handleEmailPopupClose}
        toList={teamMembers.filter(m => m.checked)}
      />
    </>
  );
};

export default D8;
